import { Component, Inject, ViewEncapsulation, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { Reward } from "../reward-selector.component";
import { GameSettingsService } from "../../game-settings.service";

@Component({
  selector: "app-reward-info",
  templateUrl: "./reward-info.component.html",
  styleUrls: ["./reward-info.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class RewardInfoComponent implements OnInit {
  dialogTitle: string = "Add Reward";
  rewardForm: FormGroup;
  imagePreview: string | null = null;
  inputError: string | null = null;
  editorForm: FormGroup | null = null;

  minExpirationTime: Date;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RewardInfoComponent>,
    public gameSettingsService: GameSettingsService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (this.data.reward) {
      this.dialogTitle = "Edit Reward";
    }
  }

  ngOnInit() {
    var self = this;

    const reward = this.data && this.data.reward ? this.data.reward : {};

    const image =
      reward.thumbnail && reward.thumbnail.image
        ? reward.thumbnail.image
        : null;

    this.rewardForm = this.fb.group({
      type: [reward.type || "link", Validators.required],
      title: [reward.title || "", Validators.required],
      description: [reward.description || "", Validators.required],
      url: [reward.url || ""],
      image: [image],
      expirationTime: [reward.expirationTime || null],
      surveyOnly: [reward.surveyOnly || false],
      isExpand: [reward.isExpand || false],
    });

    if (this.data) {
      this.rewardForm.patchValue(this.data);
      if (this.data.image) {
        this.imagePreview = image;
      }
    }

    this.setMinExpirationTime(this.data.drawDate);

    var typeControl = this.rewardForm.get("type");
    var urlControl = this.rewardForm.get("url");
    var nameControl = this.rewardForm.get("name");
    var descControl = this.rewardForm.get("description");
    var imageControl = this.rewardForm.get("image");
    var expirationTimeControl = this.rewardForm.get("expirationTime");

    typeControl.valueChanges.subscribe(function (type: string) {
      if (type === "link") {
        nameControl.setValidators([Validators.required]);
        descControl.setValidators([Validators.required]);
        imageControl.setValidators([Validators.required]);
        urlControl.setValidators([
          Validators.required,
          Validators.pattern("https?://.+"),
        ]);
        expirationTimeControl.clearValidators();
      } else {
        nameControl.clearValidators();
        descControl.clearValidators();
        imageControl.clearValidators();
        urlControl.clearValidators();
        expirationTimeControl.clearValidators();
      }
      nameControl.updateValueAndValidity();
      descControl.updateValueAndValidity();
      imageControl.updateValueAndValidity();
      urlControl.updateValueAndValidity();
      expirationTimeControl.updateValueAndValidity();
    });
  }

  minTimeValidator(minTime: Date) {
    return (control) => {
      if (!control.value || !minTime) {
        return null;
      }

      const selected = new Date(control.value);
      return selected >= minTime ? null : { minTime: true };
    };
  }

  onFileSelected(event: any) {
    var self = this;
    var fileInput = event.target;
    if (fileInput.files && fileInput.files.length > 0) {
      var file = fileInput.files[0];
      var reader = new FileReader();
      reader.onload = function (e: any) {
        self.imagePreview = e.target.result;
        self.rewardForm.patchValue({ image: self.imagePreview });
      };
      reader.readAsDataURL(file);
    }
  }

  setMinExpirationTime(drawDate: Date) {
    if (!drawDate || !this.rewardForm) {
      return;
    }

    var draw = new Date(drawDate);

    // min allowed = drawDate + 10 minutes
    var min = new Date(draw);
    min.setMinutes(min.getMinutes() + 10);
    this.minExpirationTime = min;

    var ctrl = this.rewardForm.get("expirationTime");
    
    if (!ctrl) {
      return;
    }

    var current = ctrl.value ? new Date(ctrl.value) : null;

    // Only auto-correct if there's an existing value that violates constraints
    if (current) {
      var mustUpdate = false;

      // ❌ date must match drawDate
      if (
        current.getFullYear() !== draw.getFullYear() ||
        current.getMonth() !== draw.getMonth() ||
        current.getDate() !== draw.getDate()
      ) {
        mustUpdate = true;
      }

      // ❌ must be at least +10 minutes
      if (current.getTime() < min.getTime()) {
        mustUpdate = true;
      }

      if (mustUpdate) {
        // force SAME DATE + MIN TIME
        ctrl.setValue(min);
      }
    }
    // If current is null/empty, leave it blank - don't auto-populate

    ctrl.setValidators([
      this.minTimeValidator(this.minExpirationTime),
    ]);

    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  save() {
    var self = this;
    var type = this.rewardForm.value.type;

    if (type === "link") {
      if (this.rewardForm.valid) {
        let linkResult: Reward = {
          type: "link",
          title: this.rewardForm.value.title,
          thumbnail: {
            image: this.rewardForm.value.image,
            isExpand: this.rewardForm.value.isExpand,
          },
          description: this.rewardForm.value.description,
          url: this.rewardForm.value.url,
          expirationTime: this.rewardForm.value.expirationTime,
          surveyOnly: this.rewardForm.value.surveyOnly,
        };
        if (this.data.reward && this.data.reward.id) {
          linkResult.id = this.data.reward.id;
        }
        this.dialogRef.close(linkResult);
      } else {
        this.rewardForm.markAsTouched();
      }
    } else {
      if (this.editorForm) {
        var isPositive = !!this.editorForm.value.rewardType;
        let result: Reward = {
          type: isPositive ? "positive" : "negative",
          title: isPositive ? "Positive Reward" : "Negative Reward",
          thumbnail: {
            image: this.editorForm.value.thumbnail,
            scale: this.editorForm.value.thumbnailScale
              ? this.editorForm.value.thumbnailScale
              : 1.0,
          },
          background: {
            image: this.editorForm.value.background,
            scale: this.editorForm.value.backgroundScale
              ? this.editorForm.value.backgroundScale
              : 1.0,
            color1: this.editorForm.value.backgroundColor1,
            color2: this.editorForm.value.backgroundColor2,
          },
          counter: {
            color1: this.editorForm.value.counterColor1,
            color2: this.editorForm.value.counterColor2,
          },
          coinIcon: this.editorForm.value.coinIcon,
          points: this.editorForm.value.points,
        };

        if (this.data.reward && this.data.reward.id) {
          result.id = this.data.reward.id;
        }

        this.dialogRef.close(result);
      } else {
        this.rewardForm.markAsTouched();
      }
    }
  }

  onEditorFormReady(form: FormGroup) {
    this.editorForm = form;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  onCompactApply(evt: { blob: Blob; filters: any }) {
    if (!evt || !evt.blob) return;
    this.blobToBase64(evt.blob).then((base64) => {
      this.rewardForm.patchValue({ image: base64 });
    }).catch(() => {});
  }
}
